import React, { useEffect, useState } from 'react';
import { Radio, X } from 'lucide-react';
import { useLiveDonates } from '../../hooks/live-donate/useLiveDonates';
import { useMediaQuery } from '../../hooks/use-media-query';
import { formatCa } from '../../utils/donate-alert';
import LiveDonateList from './live-donate-list';

interface LiveDonateTickerProps {
  /** Ngưỡng (px) coi là "máy tính" — phải khớp với `desktopMinWidth` của `LiveDonateFeed` đang
   *  thay thế dải này ở cùng luồng người dùng (1024 cho người đăng nhập, 1280 cho khách vãng lai). */
  desktopMinWidth?: number;
  /** Class Tailwind ẩn dải này ở màn hình máy tính — phải là literal để Tailwind sinh đúng CSS,
   *  nên chỉ nhận 2 giá trị cố định thay vì ghép chuỗi động. */
  hiddenClass?: 'lg:hidden' | 'xl:hidden';
}

/**
 * Dải chạy mỏng đầu feed (điện thoại): xoay vòng từng lượt mỗi 4 giây, bấm mở danh sách.
 *
 * Xem chú thích ở `LiveDonateFeed` — chỉ mở kết nối SignalR khi màn hình đang dưới ngưỡng
 * `desktopMinWidth` (theo dõi bằng `useMediaQuery`, tự bật/tắt lại khi cỡ màn hình đổi), để không
 * mở song song với kết nối của khung cột phải trên máy tính.
 */
const LiveDonateTicker: React.FC<LiveDonateTickerProps> = ({ desktopMinWidth = 1024, hiddenClass = 'lg:hidden' }) => {
  const isDesktop = useMediaQuery(desktopMinWidth);
  const { items, freshId } = useLiveDonates(20, !isDesktop);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);

  useEffect(() => {
    if (freshId) setIndex(0); // lượt mới lên đầu thì hiện ngay
  }, [freshId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (isDesktop || !items.length) return null;
  const d = items[Math.min(index, items.length - 1)];

  return (
    <div className={`${hiddenClass} mb-3`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ui-card flex w-full items-center gap-2 overflow-hidden px-3 py-2 text-left text-sm"
      >
        <Radio size={14} className="shrink-0 text-red-500" />
        <span className="truncate text-gray-800 dark:text-gray-100">
          <span className="font-semibold">{d.donorName}</span> donate cho{' '}
          <span className="font-semibold">{d.creatorName}</span>{' '}
          <span className="font-semibold text-amber-600 dark:text-amber-400">{formatCa(d.amount)}</span>
        </span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setOpen(false)} role="presentation">
          <div
            className="ui-card max-h-[70vh] w-full overflow-y-auto rounded-t-2xl p-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Donate trực tiếp"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800 dark:text-white">Donate trực tiếp</h3>
              <button type="button" onClick={() => setOpen(false)} aria-label="Đóng">
                <X size={18} />
              </button>
            </div>
            <LiveDonateList items={items} freshId={freshId} now={now} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveDonateTicker;
